<template>
  <div>
    <v-row id="upload">
      <v-col
        cols="12"
        class="mt-4"
      >
        <v-card
          dark
          class="upload-section"
          elevation="0"
        >
          <!-- Drop Zone -->
          <div
            v-if="!image || image.length === 0"
            class="drop-zone"
            :class="{ 'drop-zone--active': isDraggingImage }"
            @drop.prevent="onDropImage"
            @dragover.prevent="isDraggingImage = true"
            @dragleave.prevent="isDraggingImage = false"
            @click="triggerImageInput"
          >
            <div class="drop-zone-content">
              <v-icon
                color="#606060"
                size="64"
                class="mb-4"
              >
                mdi-image-outline
              </v-icon>
              <div class="drop-zone-text mb-2">
                {{ $t('gallery.image_upload.dropzone') }}
              </div>
              <div class="drop-zone-formats">
                {{ $t('gallery.image_upload.formats') }}
              </div>
            </div>
          </div>

          <!-- File Grid mit Upload Button -->
          <div
            v-else
            class="files-container"
          >
            <!-- Loading Overlay -->
            <v-overlay
              :value="isProcessingFiles"
              absolute
            >
              <div class="loading-content">
                <v-progress-circular
                  indeterminate
                  size="64"
                  color="primary"
                  class="mb-4"
                ></v-progress-circular>
                <div class="loading-text">
                  {{ $t('gallery.image_upload.processing') }}
                </div>
                <div class="loading-subtext">
                  {{ processedCount }} von {{ totalFilesToProcess }}
                </div>
              </div>
            </v-overlay>

            <!-- Header mit Info und Upload Button -->
            <div class="files-header">
              <div class="files-info">
                <v-icon
                  color="#4a9eff"
                  class="mr-2"
                >mdi-image-multiple</v-icon>
                <span class="files-count">{{ $t('gallery.image_upload.count_selected', { n: image.length }) }}</span>
                <span class="files-size ml-3">{{ totalSize }}</span>
              </div>

              <div class="files-actions">
                <v-btn
                  small
                  outlined
                  color="grey"
                  class="mr-2"
                  @click="clearAll"
                >
                  <v-icon
                    small
                    class="mr-1"
                  >mdi-close</v-icon>
                  {{ $t('gallery.image_upload.remove_all') }}
                </v-btn>
                <v-btn
                  color="green"
                  @click="imageUpload()"
                >
                  <v-icon class="mr-2">mdi-cloud-upload-outline</v-icon>
                  {{ $t('gallery.image_upload.start_upload') }}
                </v-btn>
              </div>
            </div>

            <!-- Scrollbares Grid -->
            <div class="files-grid-wrapper">
              <div class="files-grid">
                <!-- Neue Dateien hinzufügen Card -->
                <div
                  class="file-card add-more-card"
                  @click="triggerImageInput"
                >
                  <v-icon
                    size="48"
                    color="#606060"
                  >mdi-plus</v-icon>
                  <div class="add-more-text">{{ $t('gallery.image_upload.add_more') }}</div>
                </div>

                <!-- File Cards -->
                <div
                  v-for="(file, index) in image"
                  :key="index"
                  class="file-card"
                >
                  <div class="file-preview">
                    <!-- Loading State -->
                    <div
                      v-if="!previewUrls[index]"
                      class="preview-loading"
                    >
                      <v-progress-circular
                        indeterminate
                        size="32"
                        color="primary"
                      ></v-progress-circular>
                    </div>
                    <!-- Image -->
                    <img
                      v-else
                      :src="previewUrls[index]"
                      alt="preview"
                      @load="onImageLoad(index)"
                      :class="{ 'image-loaded': loadedImages[index] }"
                    />
                  </div>

                  <div class="file-info">
                    <div
                      class="file-name"
                      :title="file.name"
                    >
                      {{ truncateName(file.name) }}
                    </div>
                    <div class="file-size">
                      {{ formatFileSize(file.size) }}
                    </div>
                  </div>

                  <v-btn
                    icon
                    x-small
                    class="delete-btn"
                    @click="removeFile(index)"
                  >
                    <v-icon
                      small
                      color="red"
                    >mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Hidden File Input -->
          <v-file-input
            ref="fileInput"
            v-model="newFiles"
            accept="image/png, image/jpeg, image/jpg"
            multiple
            style="display: none"
            @change="onFileChange"
          ></v-file-input>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import APIService from '@/services/api.service';

export default {
  name: 'imageupload',

  data() {
    return {
      image: [],
      newFiles: [],
      isDraggingImage: false,
      previewUrls: {},
      loadedImages: {},
      isProcessingFiles: false,
      processedCount: 0,
      totalFilesToProcess: 0,
    }
  },

  computed: {
    ...mapGetters(["get"]),

    totalSize() {
      if (!this.image || this.image.length === 0) return '0 MB';
      const total = this.image.reduce((sum, file) => sum + file.size, 0);
      return this.formatFileSize(total);
    }
  },

  watch: {
    newFiles(files) {
      if (files && files.length > 0) {
        this.addFiles(files);
        this.newFiles = [];
      }
    }
  },

  methods: {
    async addFiles(files) {
      this.isProcessingFiles = true;
      this.totalFilesToProcess = files.length;
      this.processedCount = 0;

      const currentLength = this.image.length;

      // Files direkt zum Array hinzufügen
      this.image.push(...files);

      // Previews in Batches erstellen für bessere Performance
      const batchSize = 10;
      for (let i = 0; i < files.length; i += batchSize) {
        const batch = files.slice(i, i + batchSize);
        await this.processBatch(batch, currentLength + i);
        this.processedCount = Math.min(i + batchSize, files.length);

        // Kurze Pause zwischen Batches für UI-Updates
        await this.$nextTick();
      }

      this.isProcessingFiles = false;
    },

    async processBatch(batch, startIndex) {
      const promises = batch.map(async (file, i) => {
        const index = startIndex + i;
        try {
          // Kleinere Thumbnail-Version erstellen statt Full-Size
          const thumbnail = await this.createThumbnail(file);
          this.$set(this.previewUrls, index, thumbnail);
        } catch (error) {
          console.error('Error creating thumbnail:', error);
          // Fallback zu normalem Blob URL
          this.$set(this.previewUrls, index, URL.createObjectURL(file));
        }
      });

      await Promise.all(promises);
    },

    createThumbnail(file, maxWidth = 300, maxHeight = 300) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
          const img = new Image();

          img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            // Aspect Ratio beibehalten
            if (width > height) {
              if (width > maxWidth) {
                height *= maxWidth / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width *= maxHeight / height;
                height = maxHeight;
              }
            }

            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            resolve(canvas.toDataURL('image/jpeg', 0.7));
          };

          img.onerror = reject;
          img.src = e.target.result;
        };

        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    onImageLoad(index) {
      this.$set(this.loadedImages, index, true);
    },

    triggerImageInput() {
      this.$refs.fileInput.$refs.input.click();
    },

    onFileChange() {
      // wird durch watcher behandelt
    },

    onDropImage(e) {
      this.isDraggingImage = false;
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        this.addFiles(files);
      }
    },

    removeFile(index) {
      // Cleanup: Preview URL freigeben
      if (this.previewUrls[index]) {
        if (this.previewUrls[index].startsWith('blob:')) {
          URL.revokeObjectURL(this.previewUrls[index]);
        }
        this.$delete(this.previewUrls, index);
        this.$delete(this.loadedImages, index);
      }

      this.image.splice(index, 1);

      // Neu-indexieren der Preview URLs
      this.reindexPreviews();
    },

    reindexPreviews() {
      const newPreviewUrls = {};
      const newLoadedImages = {};

      Object.keys(this.previewUrls).forEach(key => {
        const numKey = parseInt(key);
        if (numKey < this.image.length) {
          newPreviewUrls[numKey] = this.previewUrls[key];
          newLoadedImages[numKey] = this.loadedImages[key];
        }
      });

      this.previewUrls = newPreviewUrls;
      this.loadedImages = newLoadedImages;
    },

    clearAll() {
      if (confirm(this.$t('gallery.image_upload.confirm_clear'))) {
        // Cleanup aller Preview URLs
        Object.values(this.previewUrls).forEach(url => {
          if (url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
          }
        });

        this.image = [];
        this.previewUrls = {};
        this.loadedImages = {};
      }
    },

    truncateName(name, maxLength = 20) {
      if (name.length <= maxLength) return name;
      const extension = name.split('.').pop();
      const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));
      return nameWithoutExt.substring(0, maxLength - extension.length - 4) + '...' + extension;
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    imageUpload() {
      console.log("image auf upload | ", this.image)
      console.log("neFile auf upload | ", this.newFiles)

      // Konvertiere File-Objekte in das erwartete Format mit .file Property
      const formattedImages = this.image.map(file => ({ file: file }));

      this.$store.dispatch('uploadFiles', {
        endpoint: `/api/v1/images`,
        files: { image: formattedImages },
        metadata: {
          published: false,
          price: 0,
          subscription: false,
          comment: false,
          likes: 0
        },
        message: false
      }).then(() => {
        APIService.setResponse({ message: this.$t('gallery.image_upload.upload_success'), color: "green" })
        this.$store.dispatch('fetch', { endpoint: `/api/v1/images?sort=DESC`, state: 'images' });
        this.image = []
      })
    },
  },

  mounted() {
    this.$store.dispatch('fetch', { endpoint: '/api/v1/config', state: 'config' })
    this.$store.dispatch('fetch', { endpoint: '/api/v1/stats', state: 'stats' })
  },

  beforeDestroy() {
    // Cleanup aller Preview URLs beim Verlassen der Komponente
    Object.values(this.previewUrls).forEach(url => {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
  }
}
</script>

<style scoped>
/* Drop Zone */
.drop-zone {
  border: 2px dashed #404040;
  border-radius: 8px;
  background: #0f0f0f;
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drop-zone:hover,
.drop-zone--active {
  border-color: #4a9eff;
  background: #151515;
}

.drop-zone-content {
  text-align: center;
  pointer-events: none;
}

.drop-zone-text {
  font-size: 16px;
  color: #b0b0b0;
  font-weight: 500;
}

.drop-zone-formats {
  font-size: 14px;
  color: #707070;
}

/* Loading Overlay */
.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.loading-text {
  color: #b0b0b0;
  font-size: 18px;
  font-weight: 500;
}

.loading-subtext {
  color: #707070;
  font-size: 14px;
  margin-top: 8px;
}

/* Files Container */
.files-container {
  position: relative;
  background: #0f0f0f;
  border-radius: 8px;
  padding: 20px;
}

.files-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #404040;
}

.files-info {
  display: flex;
  align-items: center;
  color: #b0b0b0;
}

.files-count {
  font-weight: 500;
  font-size: 16px;
}

.files-size {
  color: #707070;
  font-size: 14px;
}

.files-actions {
  display: flex;
  gap: 8px;
}

/* Grid Wrapper mit Scroll */
.files-grid-wrapper {
  max-height: 500px;
  overflow-y: auto;
  padding-right: 8px;
  padding-top: 4px;
  padding-bottom: 4px;
}

/* Scrollbar styling comes from the global rules in src/App.vue. */

/* Files Grid */
.files-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
}

/* File Card */
.file-card {
  position: relative;
  background: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 2px solid #2a2a2a;
}

.file-card:hover {
  border-color: #4a9eff;
  box-shadow: 0 4px 12px rgba(74, 158, 255, 0.2);
}

.file-preview {
  width: 100%;
  height: 140px;
  background: #0f0f0f;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.preview-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.file-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.file-preview img.image-loaded {
  opacity: 1;
}

.file-info {
  padding: 12px;
}

.file-name {
  color: #b0b0b0;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  color: #707070;
  font-size: 11px;
}

.delete-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7) !important;
  backdrop-filter: blur(4px);
}

/* Add More Card */
.add-more-card {
  border: 2px dashed #404040;
  background: #0f0f0f;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  cursor: pointer;
}

.add-more-card:hover {
  border-color: #4a9eff;
  background: #151515;
  box-shadow: 0 4px 12px rgba(74, 158, 255, 0.2);
}

.add-more-text {
  color: #b0b0b0;
  font-size: 14px;
  margin-top: 8px;
}
</style>